import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useContent, formatPrice } from "@/context/ContentContext";
import { resolveImage } from "@/lib/api";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Minus, ArrowRight, PackageOpen } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, count } = useCart();
  const { content } = useContent();
  const nav = useNavigate();

  const [dims, setDims] = useState({});

  useEffect(() => {
    if (items.length === 0) return;
    const ids = items.map((i) => i.product_id);
    api.post("/products/dims", { product_ids: ids }).then(({ data }) => {
      const map = {};
      data.forEach((d) => { map[d.product_id] = d; });
      setDims(map);
    }).catch(() => setDims({}));
  }, [items]);

  const totalWeight = items.reduce((sum, i) => {
    const w = dims[i.product_id]?.weight_kg || 0;
    return sum + w * (i.quantity || 1);
  }, 0);

  // Bulk-order rows: items whose qty ≥ product.bulk_qty (bulk_qty > 0)
  const bulkRows = items
    .map((it) => ({ it, bulk_qty: dims[it.product_id]?.bulk_qty || 0 }))
    .filter(({ it, bulk_qty }) => bulk_qty > 0 && it.quantity >= bulk_qty);

  const contact = content?.contact || {};
  const waNumber = contact.whatsapp_number || "917895201529";
  const waMsg = encodeURIComponent(
    `Hi TMF, I'd like a bulk quote. My cart has ${count} items totalling ${totalWeight.toFixed(1)}kg. Items: ` +
    items.map((i) => `${i.name} × ${i.quantity}`).join(", ")
  );
  const waHref = `https://wa.me/${waNumber}?text=${waMsg}`;

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">Cart</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tight mb-4 text-tmf-navy">Your cart is empty</h1>
        <p className="text-slate-600 mb-8">Browse our factory catalog and start saving today.</p>
        <Button asChild className="rounded-none bg-tmf-blue hover:bg-tmf-navy h-12 px-6 uppercase tracking-widest text-sm font-bold">
          <Link to="/products">Shop Now</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="border-b border-slate-200 pb-6 mb-8">
        <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">Checkout</div>
        <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight text-tmf-navy">Shopping Cart</h1>
        <p className="mt-2 text-sm text-slate-600">{count} items · {totalWeight.toFixed(1)} kg</p>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-8">
          <div className="border border-slate-200">
            {items.map((it, idx) => {
              const bulkThreshold = dims[it.product_id]?.bulk_qty || 0;
              const isBulk = bulkThreshold > 0 && it.quantity >= bulkThreshold;
              return (
              <div key={it.product_id} className={`p-4 md:p-6 flex gap-4 ${idx > 0 ? "border-t border-slate-200" : ""}`} data-testid={`cart-item-${it.product_id}`}>
                <div className="w-20 h-20 md:w-28 md:h-28 bg-slate-100 shrink-0 overflow-hidden">
                  {it.image ? <img src={resolveImage(it.image)} alt={it.name} className="w-full h-full object-cover" /> : null}
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <Link to={`/products/${it.product_id}`} className="font-display font-bold text-tmf-navy hover:text-tmf-blue">{it.name}</Link>
                    <div className="text-xs font-mono-tmf text-slate-500 mt-1">
                      {formatPrice(it.price, content)} · {(dims[it.product_id]?.weight_kg || 0).toFixed(1)} kg / unit
                    </div>
                    {isBulk && (
                      <div data-testid={`cart-item-bulk-${it.product_id}`} className="mt-2 inline-flex items-center gap-1.5 border border-amber-500 bg-amber-50 px-2 py-1 text-[10px] font-mono-tmf uppercase tracking-widest font-bold text-amber-800">
                        <PackageOpen size={12} /> Bulk order — we'll contact for a custom quote
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center border border-slate-300">
                      <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-2 hover:bg-slate-100"><Minus size={14} /></button>
                      <span className="px-3 font-mono-tmf text-sm">{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-2 hover:bg-slate-100"><Plus size={14} /></button>
                    </div>
                    <button data-testid={`remove-${it.product_id}`} onClick={() => removeItem(it.product_id)} className="p-2 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="font-display font-black text-tmf-navy hidden md:block">{formatPrice(it.price * it.quantity, content)}</div>
              </div>
            );})}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <div className="border border-slate-200 bg-white p-5 sticky top-24">
            <div className="font-display font-bold uppercase tracking-widest text-sm text-tmf-navy mb-4">Order Summary</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-mono-tmf font-bold">{formatPrice(subtotal, content)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Gross weight</span><span className="font-mono-tmf text-slate-500">{totalWeight.toFixed(1)} kg</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Shipping</span><span className="font-mono-tmf text-slate-500">Calculated at checkout</span></div>
              <div className="border-t border-slate-200 pt-3 flex justify-between text-base">
                <span className="font-display font-bold uppercase">Total</span>
                <span className="font-display font-black text-xl text-tmf-navy">{formatPrice(subtotal, content)}</span>
              </div>
            </div>

            {bulkRows.length > 0 && (
              <div data-testid="cart-bulk-banner" className="mt-4 border-l-4 border-amber-500 bg-amber-50 p-3 text-xs text-amber-900">
                <div className="flex items-center gap-2 font-bold mb-1 uppercase tracking-widest text-[10px]"><PackageOpen size={14} /> Bulk order detected</div>
                <div>Your cart includes {bulkRows.length} bulk-quantity {bulkRows.length === 1 ? "line" : "lines"}. You can proceed to checkout — our sales team will follow up with a personalised bulk quote (up to 25% off factory price).</div>
                <a href={waHref} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#075E54] hover:text-[#25D366]">
                  <WhatsAppIcon size={12} /> Or WhatsApp us now
                </a>
              </div>
            )}

            <Button
              data-testid="proceed-checkout-btn"
              onClick={() => nav("/checkout")}
              className="mt-4 rounded-none w-full h-12 uppercase tracking-widest text-sm font-bold bg-tmf-blue hover:bg-tmf-navy"
            >
              Proceed to Checkout
              <ArrowRight size={16} className="ml-2" />
            </Button>

            <div className="text-[10px] text-center py-3 font-mono-tmf uppercase tracking-widest text-slate-500 border-t border-slate-200 mt-3">
              Secure Razorpay checkout
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
